import React, { useState } from 'react';
import { TrendingUp, Users, Clock, Target, ChevronDown, ChevronRight, AlertCircle, CheckCircle, Plus, X } from 'lucide-react';

const Dashboard = ({ leads, setLeads, team, metrics, onSuccess }) => {
  const [showOverridePopup, setShowOverridePopup] = useState(null);
  const [overrideRep, setOverrideRep] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [showCloseMenu, setShowCloseMenu] = useState(null);
  const [expandedLeads, setExpandedLeads] = useState([]);

  // Sample leads data
  const [currentLeads, setCurrentLeads] = useState([
    {
      id: 1,
      name: 'John Smith',
      vehicle: '2024 Toyota Camry',
      confidence: 87,
      assignedTo: 'Maria Rodriguez',
      time: '5 mins ago',
      status: 'new',
      closed: false,
      actions: [
        { type: 'completed', description: 'Lead received from website form', timestamp: '5 mins ago' },
        { type: 'completed', description: 'Auto-routed to Maria (Spanish speaker)', timestamp: '5 mins ago' }
      ]
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      vehicle: '2025 Honda Accord',
      confidence: 92,
      assignedTo: 'Carlos Santos',
      time: '12 mins ago',
      status: 'contacted',
      closed: false,
      actions: [
        { type: 'completed', description: 'Lead received from phone inquiry', timestamp: '12 mins ago' },
        { type: 'completed', description: 'Auto-routed to Carlos (top performer)', timestamp: '12 mins ago' },
        { type: 'completed', description: 'First contact made - email sent', timestamp: '10 mins ago' }
      ]
    }
  ]);

  // Sample team members for override dropdown
  const teamMembers = [
    { id: 1, name: 'Maria Rodriguez' },
    { id: 2, name: 'Carlos Santos' },
    { id: 3, name: 'Vlad Petrov' },
    { id: 4, name: 'Sarah Johnson' },
    { id: 5, name: 'Mike Chen' }
  ];

  // Sample customer scenarios
  const customerScenarios = [
    { name: 'Miguel Rodriguez', vehicle: '2024 Toyota Highlander', language: 'Spanish', specialty: 'Spanish speaker' },
    { name: 'Vladimir Petrov', vehicle: '2025 Honda CR-V', language: 'Russian', specialty: 'Russian speaker' },
    { name: 'Jennifer Williams', vehicle: '2024 Ford F-150', specialty: 'Trucks' },
    { name: 'David Chen', vehicle: '2025 Tesla Model 3', specialty: 'Luxury/Electric' },
    { name: 'Amanda Martinez', vehicle: '2024 Chevrolet Silverado', specialty: 'Trucks' },
    { name: 'Robert Johnson', vehicle: '2025 BMW X5', specialty: 'Luxury' },
    { name: 'Maria Garcia', vehicle: '2024 Mazda CX-5', language: 'Spanish', specialty: 'Spanish speaker' },
    { name: 'James Wilson', vehicle: '2025 Jeep Grand Cherokee', specialty: 'SUVs' },
    { name: 'Lisa Anderson', vehicle: '2024 Subaru Outback', specialty: 'Family vehicles' }
  ];

  const simulateNewLead = () => {
    const scenario = customerScenarios[Math.floor(Math.random() * customerScenarios.length)];
    
    // Determine best rep based on specialty
    let assignedRep = 'Carlos Santos'; // default
    let confidence = 75;
    let routingReason = 'General assignment';

    if (scenario.language === 'Spanish') {
      assignedRep = 'Maria Rodriguez';
      confidence = Math.floor(Math.random() * 10) + 85; // 85-95%
      routingReason = 'Spanish-speaking customer matched to bilingual rep';
    } else if (scenario.language === 'Russian') {
      assignedRep = 'Vlad Petrov';
      confidence = Math.floor(Math.random() * 10) + 85;
      routingReason = 'Russian-speaking customer matched to native speaker';
    } else if (scenario.specialty === 'Luxury' || scenario.specialty === 'Luxury/Electric') {
      assignedRep = 'Sarah Johnson';
      confidence = Math.floor(Math.random() * 12) + 82; // 82-94%
      routingReason = 'Luxury vehicle matched to premium specialist';
    } else {
      // Random assignment with lower confidence
      const reps = ['Carlos Santos', 'Mike Chen', 'Vlad Petrov'];
      assignedRep = reps[Math.floor(Math.random() * reps.length)];
      confidence = Math.floor(Math.random() * 15) + 70; // 70-85%
      routingReason = 'Assigned to available top performer';
    }

    const newLead = {
      id: Date.now(),
      name: scenario.name,
      vehicle: scenario.vehicle,
      confidence: confidence,
      assignedTo: assignedRep,
      time: 'Just now',
      status: 'new',
      closed: false,
      actions: [
        { type: 'completed', description: 'Lead received from website form', timestamp: 'Just now' },
        { type: 'completed', description: `AI Routing (${confidence}% confidence): ${routingReason}`, timestamp: 'Just now' }
      ]
    };

    setCurrentLeads([newLead, ...currentLeads]);
    onSuccess(`New lead assigned to ${assignedRep} with ${confidence}% confidence`);
  };

  const handleManagerOverride = () => {
    if (!overrideRep || overrideReason.length < 5) {
      alert('Please select a rep and provide a reason (minimum 5 characters)');
      return;
    }

    setCurrentLeads(currentLeads.map(lead => {
      if (lead.id === showOverridePopup) {
        return {
          ...lead,
          assignedTo: overrideRep,
          actions: [
            {
              type: 'pending',
              description: `Manager override: Reassigned to ${overrideRep}. Reason: ${overrideReason}`,
              timestamp: 'Just now'
            },
            ...lead.actions
          ]
        };
      }
      return lead;
    }));

    onSuccess(`Lead reassigned to ${overrideRep}`);
    setShowOverridePopup(null);
    setOverrideRep('');
    setOverrideReason('');
  };

  const simulatePostSaleActions = async (leadId, isWin) => {
    const actions = isWin ? [
      { description: 'Congratulations email sent to customer', delay: 1000 },
      { description: 'Trade-in data retrieved from vAuto', delay: 1500 },
      { description: 'AVO form generated with trade details', delay: 2000 },
      { description: 'AVO sent to F&I department for processing', delay: 1500 },
      { description: 'Service department notified for accessories install scheduling', delay: 2000 },
      { description: '30-day follow-up reminder created for sales rep', delay: 1000 },
      { description: 'Customer added to service upsell campaign', delay: 1500 }
    ] : [
      { description: 'Loss reason logged in CRM', delay: 1000 },
      { description: 'Customer added to 90-day re-engagement campaign', delay: 1500 },
      { description: 'Thank you email sent with alternative options', delay: 2000 }
    ];

    for (const action of actions) {
      await new Promise(resolve => setTimeout(resolve, action.delay));
      
      setCurrentLeads(prev => prev.map(lead => {
        if (lead.id === leadId) {
          return {
            ...lead,
            actions: [
              { type: 'completed', description: action.description, timestamp: 'Just now' },
              ...lead.actions
            ]
          };
        }
        return lead;
      }));
    }

    onSuccess(isWin ? 'Deal closed - all post-sale actions completed' : 'Lead marked as lost - follow-up actions completed');
  };

const handleCloseDeal = async (leadId, isWin) => {
  // Add processing overlay
  setCurrentLeads(currentLeads.map(lead => {
    if (lead.id === leadId) {
      return {
        ...lead,
        processing: true
      };
    }
    return lead;
  }));

  // Wait a moment then mark as closed
  await new Promise(resolve => setTimeout(resolve, 500));

  setCurrentLeads(prev => prev.map(lead => {
    if (lead.id === leadId) {
      return {
        ...lead,
        closed: true,
        closedStatus: isWin ? 'win' : 'lost',
        status: 'closed'
      };
    }
    return lead;
  }));

  setShowCloseMenu(null);
  
  // Run post-sale actions
  await simulatePostSaleActions(leadId, isWin);

  // Remove processing overlay after all actions complete
  setCurrentLeads(prev => prev.map(lead => {
    if (lead.id === leadId) {
      return {
        ...lead,
        processing: false
      };
    }
    return lead;
  }));
};

  const toggleLeadExpansion = (leadId) => {
    setExpandedLeads(prev =>
      prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]
    );
  };

  return (
    <>
      <div className="header">
        <h1>Dashboard</h1>
        <p>Real-time lead routing and performance metrics</p>
      </div>

      {/* Metrics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '10px', background: 'rgba(0, 212, 170, 0.1)', borderRadius: '10px' }}>
              <Users size={24} color="#00D4AA" />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>Active Leads</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF' }}>24</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#00D4AA', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} />
            +12% from last week
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '10px', background: 'rgba(0, 212, 170, 0.1)', borderRadius: '10px' }}>
              <Target size={24} color="#00D4AA" />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>Avg Confidence</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF' }}>89%</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#00D4AA', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} />
            +8% improvement
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '10px', background: 'rgba(0, 212, 170, 0.1)', borderRadius: '10px' }}>
              <Clock size={24} color="#00D4AA" />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>Avg Response</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF' }}>18m</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#00D4AA', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} />
            -5m faster
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '1px solid rgba(0, 212, 170, 0.1)',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '10px', background: 'rgba(0, 212, 170, 0.1)', borderRadius: '10px' }}>
              <TrendingUp size={24} color="#00D4AA" />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>Close Rate</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF' }}>16.2%</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#00D4AA', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <TrendingUp size={14} />
            +4.2% vs baseline
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
        border: '1px solid rgba(0, 212, 170, 0.1)',
        borderRadius: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
            Recent Activity
          </h3>
          <button
            onClick={simulateNewLead}
            style={{
              background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
              border: '2px solid rgba(0, 212, 170, 0.3)',
              borderRadius: '10px',
              padding: '10px 20px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={16} />
            Simulate New Lead
          </button>
        </div>

       <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
  {currentLeads.map(lead => (
    <div
      key={lead.id}
      style={{
        background: lead.closed ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.02)',
        border: lead.closed 
          ? `2px solid ${lead.closedStatus === 'win' ? '#00D4AA' : '#ef4444'}`
          : '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '20px',
        position: 'relative'
      }}
    >
      {/* Processing Overlay */}
      {lead.processing && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10, 22, 40, 0.8)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid rgba(0, 212, 170, 0.2)',
              borderTop: '4px solid #00D4AA',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <div style={{ color: '#00D4AA', fontSize: '14px', fontWeight: '600' }}>
              Processing actions...
            </div>
          </div>
        </div>
      )}

      {/* Closed Badge */}
      {lead.closed && (
        <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '20px',
                  background: lead.closedStatus === 'win' 
                    ? 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#FFFFFF',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                }}>
                  CLOSED - {lead.closedStatus === 'win' ? 'WIN' : 'LOST'}
                </div>
              )}

              <div 
                onClick={() => toggleLeadExpansion(lead.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF', marginBottom: '4px' }}>
                      {lead.name}
                    </h4>
                    <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      {lead.vehicle}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      background: 'rgba(0, 212, 170, 0.15)',
                      border: '1px solid rgba(0, 212, 170, 0.3)',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#00D4AA',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <Target size={14} />
                      {lead.confidence}%
                    </div>

                    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                      Assigned to: <strong style={{ color: '#00D4AA' }}>{lead.assignedTo}</strong>
                    </div>

                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.4)' }}>
                      {lead.time}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {!lead.closed && (
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowOverridePopup(showOverridePopup === lead.id ? null : lead.id)}
                      style={{
                        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                        border: '2px solid rgba(251, 191, 36, 0.5)',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        color: '#78350f',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        boxShadow: '0 4px 20px rgba(251, 191, 36, 0.4)'
                      }}
                    >
                      Manager Override
                    </button>

                    {/* Override Popup */}
                    {showOverridePopup === lead.id && (
                      <div style={{
                        position: 'absolute',
                        top: '60px',
                        left: 0,
                        zIndex: 100,
                        background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
                        border: '2px solid rgba(251, 191, 36, 0.3)',
                        borderRadius: '12px',
                        padding: '20px',
                        minWidth: '350px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                          <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF', margin: 0 }}>
                            Override Assignment
                          </h4>
                          <button
                            onClick={() => setShowOverridePopup(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.5)',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <X size={18} />
                          </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                            Reassign To:
                          </label>
                          <select
                            value={overrideRep}
                            onChange={(e) => setOverrideRep(e.target.value)}
                            style={{
                              width: '100%',
                              background: '#1a2332',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              padding: '10px 14px',
                              color: '#FFFFFF',
                              fontSize: '14px'
                            }}
                          >
                            <option value="">Select team member...</option>
                            {teamMembers.map(member => (
                              <option key={member.id} value={member.name}>{member.name}</option>
                            ))}
                          </select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                            Reason (min 5 characters):
                          </label>
                          <textarea
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="e.g., Better language match, specialty expertise, workload balance..."
                            style={{
                              width: '100%',
                              background: '#1a2332',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              padding: '10px 14px',
                              color: '#FFFFFF',
                              fontSize: '14px',
                              minHeight: '80px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        <button
                          onClick={handleManagerOverride}
                          style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px',
                            color: '#FFFFFF',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Confirm Override
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setShowCloseMenu(showCloseMenu === lead.id ? null : lead.id)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Close Deal
                    </button>

                    {/* Close Menu */}
                    {showCloseMenu === lead.id && (
                      <div style={{
                        position: 'absolute',
                        top: '60px',
                        left: 0,
                        zIndex: 100,
                        background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        minWidth: '200px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                      }}>
                        <button
                          onClick={() => handleCloseDeal(lead.id, true)}
                          style={{
                            width: '100%',
                            background: 'transparent',
                            border: 'none',
                            padding: '12px 16px',
                            color: '#00D4AA',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 212, 170, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          Close as Win
                        </button>
                        <button
                          onClick={() => handleCloseDeal(lead.id, false)}
                          style={{
                            width: '100%',
                            background: 'transparent',
                            border: 'none',
                            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                            padding: '12px 16px',
                            color: '#ef4444',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          Close as Lost
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Recap (Expandable) */}
              {lead.actions && lead.actions.length > 0 && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(0, 212, 170, 0.1)' }}>
                  <div
                    onClick={() => toggleLeadExpansion(lead.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      color: '#00D4AA',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {expandedLeads.includes(lead.id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    Action History ({lead.actions.length})
                  </div>

                  {expandedLeads.includes(lead.id) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '26px' }}>
                      {lead.actions.map((action, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            padding: '12px',
                            background: action.type === 'pending' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(0, 212, 170, 0.05)',
                            borderLeft: `3px solid ${action.type === 'pending' ? '#fbbf24' : '#00D4AA'}`,
                            borderRadius: '8px',
                            fontSize: '13px'
                          }}
                        >
                          <div style={{ flexShrink: 0, marginTop: '2px' }}>
                            {action.type === 'completed' ? (
                              <CheckCircle size={16} color="#00D4AA" />
                            ) : (
                              <AlertCircle size={16} color="#fbbf24" />
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '4px' }}>
                              {action.description}
                            </div>
                            <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>
                              {action.timestamp}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
  </div>
      </div>
    </>
  );
  
};

export default Dashboard;